import { useEffect, useMemo, useState } from 'react';
import { EnquiryHandle, Product, RightPanelTabs, RightPanelTabsConfiguration } from '@access-erp/common';
import { ActionCardProps, ActionsProps, CardType, IRowNode } from '@access-erp/ui-components';

import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';

import { GetPaginatedFilteredGridContractLines }
  from '@/graphql/contracts/_generated_/getPaginatedFilteredGridContractLines.query';
import { ContractBillingStatusPascalCaseEnum } from '@/graphql/contracts/_generated_/contracts';

type ContractLineWithId = NonNullable<
NonNullable<GetPaginatedFilteredGridContractLines['paginatedFilteredGridContractLines']>['items']
>[number] & {
  id: string;
  contractId: string;
};

interface UseRightPanelParams {
  selectedHeaderRows: IRowNode<ContractLineWithId>[];
  gridRef?: EnquiryHandle<ContractLineWithId>;
  contractId: string;
  onOpenAssignModal?: () => boolean | Promise<boolean>;
  onOpenUnassignModal?: () => boolean | Promise<boolean>;
  onOpenPauseModal?: () => boolean | Promise<boolean>;
  onOpenResumeModal?: () => boolean | Promise<boolean>;
}

type ActionButton = {
  config: ActionCardProps;
  isVisible: boolean;
};

export function useRightPanel({
  selectedHeaderRows,
  onOpenAssignModal,
  onOpenUnassignModal,
  onOpenPauseModal,
  onOpenResumeModal,
}: UseRightPanelParams) {
  const [isVisibleAssignModal, setIsVisibleAssignModal] = useState(false);
  const [isVisiblePauseModal, setIsVisiblePauseModal] = useState(false);
  const [isVisibleResumeModal, setIsVisibleResumeModal] = useState(false);
  const [isVisibleUnassignModal, setIsVisibleUnassignModal] = useState(false);
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };
    getRole();
  }, [getUserRole]);

  const isUserRole = userRole === UserRole.USER;
  const isReaderRole = userRole === UserRole.READER;
  const isRestrictedRole = isUserRole || isReaderRole;

  const getSelectedLinesPauseStatus = (selectedRows: IRowNode<ContractLineWithId>[]) => {
    if (selectedRows.length === 0) return { allPaused: false, anyPaused: false, mixedStatus: false };

    const pausedCount = selectedRows.filter(row => {
      if (!row.data) return false;
      if ('lineBillingStatus' in row.data) {
        return row.data.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
          row.data.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause;
      }
      return false;
    }).length;

    const allPaused = pausedCount === selectedRows.length;
    const anyPaused = pausedCount > 0;
    const mixedStatus = anyPaused && !allPaused;

    return { allPaused, anyPaused, mixedStatus };
  };

  const pauseStatus = useMemo(() => getSelectedLinesPauseStatus(selectedHeaderRows), [selectedHeaderRows]);

  const actionButtons = useMemo<ActionButton[]>(
    () => [
      {
        config: {
          text: 'Assign Purchase Order',
          cardType: CardType.ACTION,
          product: Product.ContractsAndBilling,
          onClick: async () => {
            const canOpen = onOpenAssignModal && await onOpenAssignModal();
            if (canOpen) {
              setIsVisibleAssignModal(true);
            }
          },
        },
        isVisible: selectedHeaderRows.length > 0 && !isReaderRole,
      },
      {
        config: {
          text: 'Unassign Purchase Order',
          cardType: CardType.ACTION,
          product: Product.ContractsAndBilling,
          onClick: async () => {
            setIsVisibleUnassignModal(true);
          },
        },
        isVisible: selectedHeaderRows.length > 0 && !isReaderRole,
      },
      {
        config: {
          text: 'Pause Contract Line',
          cardType: CardType.ACTION,
          product: Product.ContractsAndBilling,
          onClick: async () => {
            const canOpen = onOpenPauseModal && await onOpenPauseModal();
            if (canOpen) {
              setIsVisiblePauseModal(true);
            }
          },
        },
        isVisible: selectedHeaderRows.length > 0 && !pauseStatus.allPaused && !isRestrictedRole,
      },
      {
        config: {
          text: 'Edit Pause Details',
          cardType: CardType.ACTION,
          product: Product.ContractsAndBilling,
          onClick: async () => {
            const canOpen = onOpenPauseModal && await onOpenPauseModal();
            if (canOpen) {
              setIsVisiblePauseModal(true);
            }
          },
        },
        isVisible: selectedHeaderRows.length > 0 && pauseStatus.allPaused && !isRestrictedRole,
      },
      {
        config: {
          text: 'Resume Contract Line',
          cardType: CardType.ACTION,
          product: Product.ContractsAndBilling,
          onClick: async () => {
            const canOpen = onOpenResumeModal && await onOpenResumeModal();
            if (canOpen) {
              setIsVisibleResumeModal(true);
            }
          },
        },
        isVisible: selectedHeaderRows.length > 0 && pauseStatus.allPaused && !isRestrictedRole,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedHeaderRows, pauseStatus, onOpenAssignModal, onOpenUnassignModal,
      onOpenPauseModal, onOpenResumeModal, isRestrictedRole],
  );

  const actionsParams: ActionsProps = {
    actionCards: {
      actions: actionButtons.filter((el) => el.isVisible).map((el) => el.config),
    },
  };

  const tabsConfiguration = useMemo<RightPanelTabsConfiguration>(
    () => ({
      [RightPanelTabs.ACTIONS]: actionsParams,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedHeaderRows],
  );

  return {
    tabsConfiguration,
    isVisibleAssignModal: isVisibleAssignModal,
    setIsVisibleAssignModal: setIsVisibleAssignModal,
    isVisiblePauseModal: isVisiblePauseModal,
    setIsVisiblePauseModal: setIsVisiblePauseModal,
    isVisibleResumeModal: isVisibleResumeModal,
    setIsVisibleResumeModal: setIsVisibleResumeModal,
    isVisibleUnassignModal: isVisibleUnassignModal,
    setIsVisibleUnassignModal: setIsVisibleUnassignModal,
  };
}
